import * as stylex from '@stylexjs/stylex';
import { useForm, useStore } from '@tanstack/react-form';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useId, useRef } from 'react';
import { DialogTrigger, ListLayout, TooltipTrigger, Virtualizer } from 'react-aria-components';
import { useAuth } from 'react-oidc-context';
import { string as vString, nonEmpty as vNonEmpty, pipe as vPipe } from 'valibot';

import { useMinDelay } from '@/hooks/use-min-delay.hook';
import {
  Button,
  ComboBox,
  type DialogProps,
  EmptyState,
  Heading,
  Hint,
  Icon,
  IconButton,
  LabeledInput,
  LinkTextButton,
  ListBox,
  ListBoxItem,
  minDelay,
  Paragraph,
  PASSWORD_INDICATOR_SLOTS,
  PasswordIndicator,
  Popover,
  ProgressIndicator,
  Tooltip,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  Refresh1StrokeIcon,
  Snackbar,
  Text,
  TextButton,
  TextField,
  tryCatch,
  useDebounce,
  usePinchZoom,
  View,
  Dialog,
  DIALOG_SLOTS,
  DialogBar,
  Image,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { organizationUserInvitationMutations, organizationUserInvitationQueries } from '@/resources/organization-user-invitation/organization-user-invitation.api';
import { userQueries } from '@/resources/user/user.api';
import { suggestPassword } from '@/utils/suggest-password.util';
import { timezone as timezoneUtil } from '@/utils/timezone.util';

export const Route = createFileRoute('/_minimal/invitations/$invitationToken')({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: `TrendMD | Create account`,
      },
    ],
  }),
  staticData: {
    minimal: {
      showLogo: true,
    },
  },
});

function RouteComponent() {
  const id = useId();
  const auth = useAuth();
  const routeParams = Route.useParams();

  const hasSignedOutRef = useRef(false);
  useEffect(() => {
    if (!hasSignedOutRef.current && auth.isAuthenticated) {
      hasSignedOutRef.current = true;
      auth.signoutRedirect().then(auth.removeUser);
    }
  }, [ auth ]);

  const validateInvitationQuery = useQuery({
    ...organizationUserInvitationQueries.validate(routeParams.invitationToken),
    refetchOnWindowFocus: false,
  });
  const acceptUserInvitationMutation = useMutation(organizationUserInvitationMutations.accept);

  const delayedValidateInvitationQueryData = useMinDelay(
    validateInvitationQuery.isLoading,
    validateInvitationQuery.data,
    PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  );

  const form = useForm({
    defaultValues: {
      email: delayedValidateInvitationQueryData.data?.email ?? '',
      password: suggestPassword(),
      firstName: '',
      lastName: '',
      timezone: timezoneUtil.findCurated(timezoneUtil.normalize(Intl.DateTimeFormat().resolvedOptions().timeZone))?.id ?? '',
    },
    onSubmit: async ({ value }) => {
      const response = await minDelay(tryCatch(acceptUserInvitationMutation.mutateAsync({
        token: routeParams.invitationToken,
        body: {
          firstName: value.firstName,
          lastName: value.lastName,
          password: value.password,
          timezone: timezoneUtil.getIana(value.timezone),
        },
      })), PROGRESS_INDICATOR_MIN_DELAY_DURATION);

      if (!response.error) {
        await auth.signinRedirect();
      }
    },
  });

  const debouncedPassword = useDebounce(useStore(form.store, (state) => state.values.password), 500);
  const getPasswordStrengthQuery = useQuery({
    ...userQueries.getPasswordStrength({ password: debouncedPassword }),
    enabled: debouncedPassword !== form.options.defaultValues?.password,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      {!delayedValidateInvitationQueryData.pending && !delayedValidateInvitationQueryData.data?.email ?
        <EmptyState style={emptyStateStyles.base}>
          <Heading>Couldn't verify invitation</Heading>
          <Paragraph>Please ask your organization admin to send you a new invitation link</Paragraph>
        </EmptyState>
        : <View style={styles.base}>
          {delayedValidateInvitationQueryData.pending &&
            <Snackbar style={snackbarStyles.base} variant="contrast">
              <ProgressIndicator variant="inverted" />
              <Text>Verifying invitation</Text>
            </Snackbar>}
          <form
            id={id}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            {...stylex.props(formStyles.base)}>
            <Heading level={1} size={28}>Create account</Heading>
            <form.Field name="email">
              {(field) => {
                const isDirtyAndInvalid = field.getMeta().isDirty && field.state.meta.errors.length > 0;
                return (
                  <TextField
                    autoComplete="off"
                    autoFocus
                    id={field.name}
                    isDisabled
                    isInvalid={isDirtyAndInvalid}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}>
                    <LabeledInput label="Email" />
                  </TextField>
                );
              }}
            </form.Field>

            <View style={passwordFieldStyles.view}>
              <form.Field name="password" validators={{ onSubmit: vPipe(vString(), vNonEmpty('Enter password')) }}>
                {(field) => {
                  const isDirtyAndInvalid = field.getMeta().isDirty && field.state.meta.errors.length > 0;
                  return (
                    <>
                      <TextField
                        autoComplete="off"
                        autoFocus
                        id={field.name}
                        isDisabled={delayedValidateInvitationQueryData.pending}
                        isInvalid={isDirtyAndInvalid}
                        name={field.name}
                        type="text"
                        value={delayedValidateInvitationQueryData.pending ? '' : field.state.value}
                        onBlur={field.handleBlur}
                        onChange={field.handleChange}>
                        <LabeledInput label="Password">
                          {(renderProps) =>
                            <>
                              {renderProps.defaultChildren}
                              {!delayedValidateInvitationQueryData.pending && field.state.value.length > 0 &&
                              <TooltipTrigger>
                                <IconButton
                                  aria-label="Generate new password"
                                  excludeFromTabOrder
                                  size={32}
                                  variant="ghost"
                                  onPress={() => field.handleChange(suggestPassword())}>
                                  <Refresh1StrokeIcon />
                                </IconButton>
                                <Tooltip>Generate new password</Tooltip>
                              </TooltipTrigger>}
                            </>}
                        </LabeledInput>
                        {isDirtyAndInvalid && <Hint><Icon />{field.state.meta.errors[0]?.message}</Hint>}
                      </TextField>
                      {!delayedValidateInvitationQueryData.pending &&
                      <>
                        <PasswordIndicator style={passwordFieldStyles.indicator} value={getPasswordStrengthQuery.data?.score ?? 4}>
                          {(renderProps) =>
                            <>
                              {renderProps.defaultChildren}
                              <Text slot={PASSWORD_INDICATOR_SLOTS.DESCRIPTION}>{renderProps.valueText}</Text>
                            </>}
                        </PasswordIndicator>
                        <DialogTrigger>
                          <TextButton size={28} style={textButtonStyles.base} variant="tertiary">
                            Why is there a pre-generated four-word password?
                          </TextButton>
                          <FourWordPasswordExplanationDialog />
                        </DialogTrigger>
                      </>}
                    </>);
                }}
              </form.Field>
            </View>

            <form.Field name="firstName" validators={{ onMount: vPipe(vString(), vNonEmpty('Enter first name')), onSubmit: vPipe(vString(), vNonEmpty('Enter first name')) }}>
              {(field) => {
                const isDirtyAndInvalid = field.getMeta().isDirty && field.state.meta.errors.length > 0;
                return (
                  <TextField
                    autoComplete="off"
                    id={field.name}
                    isDisabled={delayedValidateInvitationQueryData.pending}
                    isInvalid={isDirtyAndInvalid}
                    name={field.name}
                    type="text"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}>
                    <LabeledInput
                      data-1p-ignore
                      data-bwignore
                      data-form-type="other"
                      data-lpignore="true"
                      data-protonpass-ignore
                      label="First name"
                    />
                    {isDirtyAndInvalid && <Hint><Icon />{field.state.meta.errors[0]?.message}</Hint>}
                  </TextField>
                );
              }}
            </form.Field>

            <form.Field name="lastName" validators={{ onMount: vPipe(vString(), vNonEmpty('Enter last name')), onSubmit: vPipe(vString(), vNonEmpty('Enter last name')) }}>
              {(field) => {
                const isDirtyAndInvalid = field.getMeta().isDirty && field.state.meta.errors.length > 0;
                return (
                  <TextField
                    autoComplete="off"
                    id={field.name}
                    isDisabled={delayedValidateInvitationQueryData.pending}
                    isInvalid={isDirtyAndInvalid}
                    name={field.name}
                    type="text"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}>
                    <LabeledInput
                      data-1p-ignore
                      data-bwignore
                      data-form-type="other"
                      data-lpignore="true"
                      data-protonpass-ignore
                      label="Last name"
                    />
                    {isDirtyAndInvalid && <Hint><Icon />{field.state.meta.errors[0]?.message}</Hint>}
                  </TextField>
                );
              }}
            </form.Field>

            <form.Field name="timezone">
              {(field) =>
                <ComboBox
                  id={field.name}
                  isDisabled={delayedValidateInvitationQueryData.pending}
                  name={field.name}
                  selectedKey={field.state.value || null}
                  onSelectionChange={(key) => {
                    const selected = key != null ? String(key) : '';
                    field.handleChange(selected);
                  }}>
                  <LabeledInput label="Timezone" placeholder="Select timezone" size={48} />
                  <Popover>
                    <Virtualizer layout={ListLayout} layoutOptions={{ rowHeight: 40 }}>
                      <ListBox items={timezoneUtil.list()}>
                        {(item) =>
                          <ListBoxItem id={item.id} textValue={item.label}>
                            {item.label}
                          </ListBoxItem>}
                      </ListBox>
                    </Virtualizer>
                  </Popover>
                </ComboBox>}
            </form.Field>

            <form.Subscribe
              selector={(state) => [ state.isSubmitting, state.canSubmit, state.values.password ] as const}>
              {([ isSubmitting, canSubmit, password ]) => {
                const score = getPasswordStrengthQuery.data?.score;
                const isPasswordStrong = score != null ? score >= 3 : password === form.options.defaultValues?.password;
                const isPasswordValid = isPasswordStrong && password.length >= 8;

                return (
                  <Button
                    form={id}
                    isDisabled={!canSubmit || !isPasswordValid || delayedValidateInvitationQueryData.pending}
                    isPending={isSubmitting}
                    isRounded
                    size={48}
                    type="submit">
                  Sign up
                  </Button>
                );
              }}
            </form.Subscribe>
          </form>
        </View>}
      <footer {...stylex.props(footerStyles.base)}>
        <LinkTextButton href="https://www.trendmd.com/terms/" rel="noopener noreferrer" safeArea="square" size={28} target="_blank" variant="tertiary">Privacy and terms</LinkTextButton>
      </footer>
    </>
  );
}

function FourWordPasswordExplanationDialog(props: DialogProps) {
  const {
    pinchZoomTransitionX,
    pinchZoomTransitionY,
    onMouseDown,
    onDragStart,
    zoom,
    onTouchMove,
    onTouchEnd,
    onMouseWheel,
  } = usePinchZoom({
    maxZoom: 7,
    relativeTo: 'parent',
  });

  return (
    <Dialog {...props} style={fourWordPasswordDialogStyles.base}>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR} style={topBarStyles.base}>
        <Heading
          color="primary"
          size={24}
          slot={DIALOG_SLOTS.TITLE}>
          Four-word passwords are easy
          to remember yet harder to hack
        </Heading>
        <IconButton aria-label="Close" slot={DIALOG_SLOTS.CLOSE} />
      </DialogBar>
      <View style={comicStyles.container}>
        <picture {...stylex.props(comicStyles.picture)}>
          <Image
            alt="Four-word password comic explaining password strength"
            src="/four-word-password-comic.webp"
            style={comicStyles.image({ zoom, pinchZoomTransitionX, pinchZoomTransitionY })}
            onMouseDown={onMouseDown}
            onTouchEnd={onTouchEnd}
            onTouchMove={onTouchMove}
            onTouchStart={onDragStart}
            onWheel={onMouseWheel}
          />
        </picture>
      </View>
      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR} style={bottomBarStyles.base}>
        <LinkTextButton href="https://xkcd.com/936/" target="_blank" variant="tertiary">https://xkcd.com/936/</LinkTextButton>
      </DialogBar>
    </Dialog>
  );
}

const fourWordPasswordDialogStyles = stylex.create({
  base: {
    margin: 8,
    width: 500,
  },
});

const topBarStyles = stylex.create({
  base: {
  },
});

const comicStyles = stylex.create({
  container: {
    overflow: 'hidden',
  },
  picture: {
    display: 'block',
    marginInline: 'auto',
    maxWidth: '100%',
  },
  image: (props: { zoom: number; pinchZoomTransitionX: number; pinchZoomTransitionY: number }) => ({
    display: 'block',
    height: 'auto',
    transform: `scale(${props.zoom}) translateX(${props.pinchZoomTransitionX}px) translateY(${props.pinchZoomTransitionY}px)`,
    width: '100%',
  }),
});

const bottomBarStyles = stylex.create({
  base: {
    justifyContent: 'center',
  },
});

const styles = stylex.create({
  base: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 16,
    maxWidth: 432,
    paddingBottom: {
      default: 12,
      [breakpoints.large]: 32,
    },
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 75,
    width: '100%',
  },
});

const emptyStateStyles = stylex.create({
  base: {
    marginBottom: 'auto',
    paddingTop: 122,
  },
});

const formStyles = stylex.create({
  base: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});

const footerStyles = stylex.create({
  base: {
    alignContent: 'flex-start',
    alignItems: 'flex-start',
    bottom: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    marginTop: 'auto',
    paddingBlock: 2,
  },
});

const passwordFieldStyles = stylex.create({
  view: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  indicator: {
    paddingInline: 16,
    width: '100%',
  },
});

const textButtonStyles = stylex.create({
  base: {
    marginBottom: 8,
    marginLeft: 14,
    width: 'fit-content',
  },
});

const snackbarStyles = stylex.create({
  base: {
    alignSelf: 'center',
    gap: 10,
    maxWidth: 338,
    minWidth: 0,
    position: 'absolute',
    top: {
      default: 60,
      [breakpoints.small]: 45,
    },
    width: '100%',
  },
});
